import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";

const SUPER_ADMIN_ROUTES = new Set([
  "/admin",
  "/administracion",
  "/security",
  "/investor",
  "/contracts",
  "/license-center",
  "/deployment",
  "/global-crm",
  "/multi-tenant",
]);

export function isSuperAdminRoute(pathname: string): boolean {
  return SUPER_ADMIN_ROUTES.has(pathname);
}

/**
 * CRITICAL SECURITY HOTFIX — Server-side RBAC guard.
 *
 * Replaces the legacy localStorage `ss26.superAdmin` opt-in flag with a real
 * authorization check that requires:
 *   1. A live Supabase auth session, and
 *   2. The `superadmin` (or `admin`) role in `public.user_roles`.
 *
 * Returns a Promise so it can be awaited inside an async `beforeLoad`.
 * Tenant-impersonation sessions are also denied super-admin routes.
 */
export async function requireSuperAdminGuard(pathname: string): Promise<void> {
  if (typeof window === "undefined") return;

  const isTenant = (() => {
    try {
      return Boolean(localStorage.getItem("ss26.currentTenantImpersonation"));
    } catch {
      return false;
    }
  })();

  let isSuper = false;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData.session) {
      const { data, error } = await supabase.rpc("is_superadmin");
      if (!error) isSuper = Boolean(data);
    }
  } catch {
    isSuper = false;
  }

  if (!isSuper || isTenant) {
    void trackEvent("tenant_isolation_enforced", {
      pathname,
      reason: !isSuper ? "not_super_admin" : "tenant_session",
      enforced_by: "supabase_rbac",
    });
    throw redirect({ to: "/tenant-portal", search: { tenant: undefined } });
  }
}